/**
 * Shared persisted shape & migration for sframe-160 (+ /sframe-161 business & valuations UI).
 */

const nid = () => `n-${Math.random().toString(36).slice(2, 9)}`;

const NORMALIZED_VAL_STATUSES = [
  'Needs Valuation',
  'Professional Engaged',
  'In Progress',
  'Completed',
] as const;

/* ---------- legacy personal categories (kept for migration) ---------- */

export const PERSONAL_CATEGORIES = [
  'Jewelry',
  'Art',
  'Furniture',
  'Electronics',
  'Collectibles',
  'Musical instruments',
  'Sports equipment',
  'Books & media',
  'Other',
] as const;

/* ---------- unified property item types (new) ---------- */

export type PropertyItemType =
  | 'physical_assets'
  | 'collectibles_valuables'
  | 'electronics'
  | 'digital_assets'
  | 'custom';

export type PropertyValuationMethod = 'Estimated' | 'Market Value' | 'Appraised' | 'Unknown';
export type PropertyAppraisalStatus = 'Not Started' | 'In Progress' | 'Completed';
export type PropertyAccessStatus = 'Has access' | 'No access' | 'Unknown';
export type PropertyIntendedAction =
  | 'Retain'
  | 'Distribute'
  | 'Transfer'
  | 'Sell'
  | 'Close'
  | 'Memorialize'
  | 'Unknown';
export type PropertyExecutorStatus = 'Needs review' | 'In Progress' | 'Completed';

export const PROPERTY_ITEM_TYPES: readonly { value: PropertyItemType; label: string }[] = [
  { value: 'physical_assets', label: 'Physical Assets' },
  { value: 'collectibles_valuables', label: 'Collectibles & Valuables' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'digital_assets', label: 'Digital Assets' },
  { value: 'custom', label: 'Custom Item' },
] as const;

export interface UnifiedPropertyItem {
  id: string;
  type: PropertyItemType;
  name: string;
  description: string;
  estimatedValue: string;
  valuationMethod: PropertyValuationMethod;
  needsAppraisal: boolean;
  appraisalStatus: PropertyAppraisalStatus;
  location: string;
  handlingInstructions: string;
  notes: string;
  accessStatus: PropertyAccessStatus;
  intendedAction: PropertyIntendedAction;
  executorProgressStatus: PropertyExecutorStatus;
}

/* ---------- legacy row types (kept for migration / SFrame160Business compat) ---------- */

export interface PersonalItemRow {
  id: string;
  name: string;
  category: string;
  description: string;
  value: string;
  location: string;
  recipient: string;
  notes: string;
  status: string;
}

export interface DigitalAssetRow {
  id: string;
  platform: string;
  accountType: string;
  accessStatus: 'Known' | 'Unknown' | 'None';
  intendedAction: 'Close' | 'Transfer' | 'Memorialized' | 'Retain';
  notes: string;
  status: string;
}

export interface ValuationTableRow {
  id: string;
  assetRef: string;
  assetType: string;
  name: string;
  valuationType: 'CPA' | 'Appraiser' | 'Self' | '';
  professional: string;
  status: 'Needs Valuation' | 'Professional Engaged' | 'In Progress' | 'Completed';
  dodValue: string;
  reportFileName: string;
  reportFileSize: number;
  dodConfirmed: boolean;
  complete: boolean;
}

export interface BusinessInterestRow {
  id: string;
  entityName: string;
  ownershipPct: string;
  role: string;
  advisorContact: string;
  notes: string;
}

export interface SFrame160Persisted {
  items: UnifiedPropertyItem[];
  personalItems: PersonalItemRow[];
  digitalAssets: DigitalAssetRow[];
  valuationTable: ValuationTableRow[];
  businessInterests: BusinessInterestRow[];
}

/* ---------- legacy types for migration ---------- */

interface LegacyPersonalRow {
  id: string;
  item: string;
  location: string;
  estimatedValue: string;
  photoReference: string;
}

interface LegacyBusinessRow {
  id: string;
  entityName: string;
  ownershipPct: string;
  role: string;
  advisorContact: string;
  notes: string;
}

/* ---------- helpers ---------- */

function normalizeStatus(s: unknown): ValuationTableRow['status'] {
  if (typeof s !== 'string') return 'Needs Valuation';
  return (NORMALIZED_VAL_STATUSES as readonly string[]).includes(s)
    ? (s as ValuationTableRow['status'])
    : 'Needs Valuation';
}

function personalCategoryToType(cat: string): PropertyItemType {
  const lower = cat.toLowerCase();
  if (['jewelry', 'art', 'collectibles'].some((k) => lower.includes(k))) return 'collectibles_valuables';
  if (lower.includes('electronics')) return 'electronics';
  return 'physical_assets';
}

function migratePersonalToUnified(row: PersonalItemRow): UnifiedPropertyItem {
  return {
    id: row.id || nid(),
    type: personalCategoryToType(row.category),
    name: row.name || '',
    description: row.description || '',
    estimatedValue: row.value || '',
    valuationMethod: 'Unknown',
    needsAppraisal: false,
    appraisalStatus: 'Not Started',
    location: row.location || '',
    handlingInstructions: row.recipient ? `Intended recipient: ${row.recipient}` : '',
    notes: row.notes || '',
    accessStatus: 'Unknown',
    intendedAction: 'Unknown',
    executorProgressStatus: 'Needs review',
  };
}

const DIGITAL_ACCESS_MAP: Record<string, PropertyAccessStatus> = {
  Known: 'Has access',
  Unknown: 'Unknown',
  None: 'No access',
};

const DIGITAL_ACTION_MAP: Record<string, PropertyIntendedAction> = {
  Close: 'Close',
  Transfer: 'Transfer',
  Memorialized: 'Memorialize',
  Retain: 'Retain',
};

function migrateDigitalToUnified(row: DigitalAssetRow): UnifiedPropertyItem {
  return {
    id: row.id || nid(),
    type: 'digital_assets',
    name: row.platform || '',
    description: row.accountType || '',
    estimatedValue: '',
    valuationMethod: 'Unknown',
    needsAppraisal: false,
    appraisalStatus: 'Not Started',
    location: '',
    handlingInstructions: '',
    notes: row.notes || '',
    accessStatus: DIGITAL_ACCESS_MAP[row.accessStatus] ?? 'Unknown',
    intendedAction: DIGITAL_ACTION_MAP[row.intendedAction] ?? 'Unknown',
    executorProgressStatus: 'Needs review',
  };
}

function coerceUnifiedItem(row: Partial<UnifiedPropertyItem> & Record<string, unknown>): UnifiedPropertyItem {
  const VALID_TYPES: PropertyItemType[] = ['physical_assets', 'collectibles_valuables', 'electronics', 'digital_assets', 'custom'];
  const VALID_VAL: PropertyValuationMethod[] = ['Estimated', 'Market Value', 'Appraised', 'Unknown'];
  const VALID_APPR: PropertyAppraisalStatus[] = ['Not Started', 'In Progress', 'Completed'];
  const VALID_ACCESS: PropertyAccessStatus[] = ['Has access', 'No access', 'Unknown'];
  const VALID_ACTION: PropertyIntendedAction[] = ['Retain', 'Distribute', 'Transfer', 'Sell', 'Close', 'Memorialize', 'Unknown'];
  const VALID_EXEC: PropertyExecutorStatus[] = ['Needs review', 'In Progress', 'Completed'];

  return {
    id: String(row.id || nid()),
    type: VALID_TYPES.includes(row.type as PropertyItemType) ? (row.type as PropertyItemType) : 'physical_assets',
    name: typeof row.name === 'string' ? row.name : '',
    description: typeof row.description === 'string' ? row.description : '',
    estimatedValue: typeof row.estimatedValue === 'string' ? row.estimatedValue : '',
    valuationMethod: VALID_VAL.includes(row.valuationMethod as PropertyValuationMethod) ? (row.valuationMethod as PropertyValuationMethod) : 'Unknown',
    needsAppraisal: !!row.needsAppraisal,
    appraisalStatus: VALID_APPR.includes(row.appraisalStatus as PropertyAppraisalStatus) ? (row.appraisalStatus as PropertyAppraisalStatus) : 'Not Started',
    location: typeof row.location === 'string' ? row.location : '',
    handlingInstructions: typeof row.handlingInstructions === 'string' ? row.handlingInstructions : '',
    notes: typeof row.notes === 'string' ? row.notes : '',
    accessStatus: VALID_ACCESS.includes(row.accessStatus as PropertyAccessStatus) ? (row.accessStatus as PropertyAccessStatus) : 'Unknown',
    intendedAction: VALID_ACTION.includes(row.intendedAction as PropertyIntendedAction) ? (row.intendedAction as PropertyIntendedAction) : 'Unknown',
    executorProgressStatus: VALID_EXEC.includes(row.executorProgressStatus as PropertyExecutorStatus) ? (row.executorProgressStatus as PropertyExecutorStatus) : 'Needs review',
  };
}

/* ---------- main migration ---------- */

export function migrateSFrame160Data(raw: Record<string, unknown> | undefined): SFrame160Persisted {
  const d = raw ?? {};

  // --- unified items (new canonical format) ---
  let items: UnifiedPropertyItem[] | null = null;

  if (Array.isArray(d.items) && d.items.length > 0) {
    items = (d.items as Partial<UnifiedPropertyItem>[]).filter(Boolean).map((r) => coerceUnifiedItem({ ...r, id: (r as Record<string, unknown>).id || nid() }));
  }

  // --- legacy personal items ---
  let personalItems = (d.personalItems as PersonalItemRow[] | undefined)?.filter(Boolean);

  if (!personalItems?.length && Array.isArray(d.personal)) {
    const legacy = d.personal as LegacyPersonalRow[];
    personalItems = legacy.map((r) => ({
      id: r.id || nid(),
      name: r.item || '',
      category: 'Other',
      description: r.photoReference ? `Photo / ref: ${r.photoReference}` : '',
      value: r.estimatedValue || '',
      location: r.location || '',
      recipient: '',
      notes: '',
      status: 'Needs review',
    }));
  }

  // --- legacy digital assets ---
  const digitalAssetsRaw =
    ((d.digitalAssets as unknown) as Partial<DigitalAssetRow>[] | undefined | null)?.filter(Boolean) ?? [];

  // --- if no new-format items, migrate from legacy arrays ---
  if (!items) {
    const fromPersonal = (personalItems ?? []).map(migratePersonalToUnified);
    const fromDigital = digitalAssetsRaw.map((r) => {
      const coerced = coerceDigitalLegacy({ ...r, id: r.id || nid() });
      return migrateDigitalToUnified(coerced);
    });
    items = [...fromPersonal, ...fromDigital];
  }

  // --- business interests ---
  let businessInterests = (d.businessInterests as BusinessInterestRow[] | undefined)?.filter(Boolean);

  if (!businessInterests?.length && Array.isArray(d.business)) {
    businessInterests = (d.business as LegacyBusinessRow[]).map((r) => ({
      id: r.id || nid(),
      entityName: r.entityName || '',
      ownershipPct: r.ownershipPct || '',
      role: r.role || '',
      advisorContact: r.advisorContact || '',
      notes: r.notes || '',
    }));
  }

  // --- valuation table ---
  const valuationTableRaw = (d.valuationTable as ValuationTableRow[] | undefined)?.filter(Boolean);

  const valuationTable: ValuationTableRow[] =
    valuationTableRaw?.map((r) => ({
      id: r.id || nid(),
      assetRef: r.assetRef || `ref-${r.id || nid()}`,
      assetType: r.assetType || 'Asset',
      name: r.name || '',
      valuationType:
        r.valuationType === 'CPA' || r.valuationType === 'Appraiser' || r.valuationType === 'Self' ? r.valuationType : '',
      professional: typeof r.professional === 'string' ? r.professional : '',
      status: normalizeStatus(r.status),
      dodValue: typeof r.dodValue === 'string' ? r.dodValue : '',
      reportFileName: typeof r.reportFileName === 'string' ? r.reportFileName : '',
      reportFileSize: typeof r.reportFileSize === 'number' ? r.reportFileSize : 0,
      dodConfirmed: !!r.dodConfirmed,
      complete: !!r.complete,
    })) ?? [];

  return {
    items,
    personalItems: [],
    digitalAssets: [],
    valuationTable,
    businessInterests: businessInterests?.length ? businessInterests.map((r) => ({ ...r, id: r.id || nid() })) : [],
  };
}

/* legacy digital coerce (kept for migration path) */
function coerceDigitalLegacy(row: Partial<DigitalAssetRow> & Record<string, unknown>): DigitalAssetRow {
  const a = row.accessStatus;
  const access: DigitalAssetRow['accessStatus'] =
    a === 'Known' || a === 'Unknown' || a === 'None' ? a : 'Unknown';
  const ia = row.intendedAction;
  const intended: DigitalAssetRow['intendedAction'] =
    ia === 'Close' || ia === 'Transfer' || ia === 'Memorialized' || ia === 'Retain' ? ia : 'Retain';
  return {
    id: String(row.id || nid()),
    platform: typeof row.platform === 'string' ? row.platform : '',
    accountType: typeof row.accountType === 'string' ? row.accountType : '',
    accessStatus: access,
    intendedAction: intended,
    notes: typeof row.notes === 'string' ? row.notes : '',
    status: typeof row.status === 'string' ? row.status : 'Needs review',
  };
}
